"use client";

import { useContext, useState, useEffect } from "react";

import { QRSelectorGrid } from "@/components/QRSelectorGrid";
import { Typography } from "@/components/Typography";

import { useQR } from "@/hooks/useQR";
import { useToast } from "@/hooks/useToast";
import { handleNextButton } from "@/utils/qrEditorNavigatorHelper";
import { setNewQRData, generateQrDataClone } from "@/utils/qrEditorHelper";

import { UserContext } from "@/contexts/UserContext";
import { GlobalContext } from "@/contexts/GlobalContext";
import { QREditorContext } from "@/contexts/QREditorContext";
import { QREditorContextProps } from "@/contexts/QREditorContext/types";

import { QRType, QRTypeName } from "@/types/qr";
import { QREditorSelectorProps } from "./types";
import { useRouter } from "next/navigation";
import { AmplitudeContext } from "@/contexts/AmplitudeContext/AmplitudeContext";

import "./styles.scss";
import { amplitudeTrackEvent } from "@/services/AmplitudeService/AmplitudeService";

export const QREditorSelectorTemplate = (props: QREditorSelectorProps) => {
  const { data } = props;
  const { toast } = useToast();
  const router = useRouter();

  const userContext = useContext(UserContext);
  const { source, setSource } = useContext(AmplitudeContext);
  const qrEditorContext =
    useContext<QREditorContextProps<any>>(QREditorContext);
  const globalContext = useContext(GlobalContext);
  const [qrTypeSelected, setQrTypeSelected] = useState<QRType | string>("");
  const [isQRSelected, setIsQRSelected] = useState(false);

  const { requestQR } = useQR();

  const hoverChangeQRType = (type: QRType, name: QRTypeName) => {
    setQrTypeSelected(type);
    if (!isQRSelected) setNewQRData(qrEditorContext, type, name);
  };

  const onClickQRselect = (type: QRType, name: QRTypeName) => {
    hoverChangeQRType(type, name);
    setIsQRSelected(true);
    setSource("cta_buttons");
    qrEditorContext?.setEventSource("qr-editor");
  };

  useEffect(() => {
    setIsQRSelected(false);
    qrEditorContext?.setValidationError(false);
    const newQRData = generateQrDataClone(qrEditorContext, "");
    if (source) {
      amplitudeTrackEvent("view_editor_select_qr ", {
        source,
      });
    } else {
      amplitudeTrackEvent("view_editor_select_qr", {
        source: "browser_url",
      });
    }
    if (newQRData) newQRData.currentPageIndex = 0;
    setTimeout(() => {
      qrEditorContext?.setQrData(newQRData);
    }, 0);
  }, []);

  useEffect(() => {
    if (isQRSelected) {
      setTimeout(() => {
        handleNextButton(
          globalContext,
          qrEditorContext,
          userContext,
          requestQR,
          toast,
          true,
          router
        );
      }, 0);
    }
  }, [isQRSelected]);

  useEffect(() => {
    if (source) setNewQRData(qrEditorContext, qrTypeSelected);
  }, [source]);

  return (
    <div className="qr-selector" data-testid="QRHOme">
      <div className="qr-selector__layout">
        <Typography Tag="p" size="xl" classname="u-bold u-spacing-5">
          {data?.title}
        </Typography>

        <QRSelectorGrid
          data={data}
          onHoverChangeQRType={(id, title) => hoverChangeQRType(id, title)}
          onClickQRselect={(id, title) => onClickQRselect(id, title)}
        />
      </div>
    </div>
  );
};
